/**
 * Virement de trésorerie entre deux comptes KESTIO.
 *
 * Module PUR, importé aussi par le frontend : l'écran et le serveur doivent
 * refuser les mêmes saisies et annoncer la même référence.
 */
import { depuisIso, type DateIso } from './dates';
import { estBanqueVirement, BANQUES_VIREMENT } from './banques-virement';
import { BANKS } from './reference-data';

export const ORIGINE_VIREMENT_INTERNE = 'virement_interne';
export const TYPE_CHARGE_VIREMENT_INTERNE = 'Virement interne';

/**
 * Les comptes qui peuvent RECEVOIR : ceux de KESTIO, LCL compris. Ils ne se
 * confondent pas avec `BANQUES_VIREMENT`, les seuls d'où Gertrude sait émettre.
 */
export const COMPTES_INTERNES = BANKS;
export type CompteInterne = (typeof BANKS)[number];

export function estCompteInterne(valeur: unknown): valeur is CompteInterne {
  return typeof valeur === 'string' && (COMPTES_INTERNES as readonly string[]).includes(valeur);
}

/**
 * Le titulaire tel que chaque banque l'a enregistré (Valérie, 04/11/2026). Les
 * banques comparent ce nom à celui du compte : « KESTIO BNP » déclencherait une
 * alerte, d'où un nom d'affichage et un titulaire distincts.
 */
export const TITULAIRES_COMPTES: Record<CompteInterne, string> = {
  BNP: 'SAS KESTIO',
  CIC: 'KESTIO',
  LCL: 'KESTIO',
  QONTO: 'KESTIO',
};

export function nomBeneficiaireInterne(compte: CompteInterne): string {
  return `KESTIO ${compte}`;
}

/**
 * Sert de numéro de ligne et d'identifiant dans le fichier bancaire (35 caractères
 * au plus). La date et les banques la rendent unique : deux lignes de même numéro
 * ne peuvent pas être en cours. Le montant n'y figure pas : un second virement du
 * même jour entre les mêmes comptes attend donc que le premier soit réglé, quel que
 * soit son montant, ou se date du lendemain.
 */
export function referenceVirementInterne(date: DateIso, emettrice: string, beneficiaire: string): string {
  return `VIREMENT INTERNE ${date.replaceAll('-', '')} ${emettrice}-${beneficiaire}`;
}

export function motifVirementInterne(emettrice: string, beneficiaire: string): string {
  return `Virement interne : ${emettrice} vers ${beneficiaire}`;
}

/**
 * La date demandée à la banque pour un virement interne (Valérie, 05/11/2026) : sa
 * date, ou le jour de l'export si elle est dépassée. Sa référence garde la date
 * d'origine. Une ligne validée ne se corrige plus, et la réaction de chaque banque
 * à une date passée est inconnue.
 */
export function dateExecutionInterne(datePaiement: DateIso | null, aujourdhui: DateIso): DateIso {
  return datePaiement && datePaiement > aujourdhui ? datePaiement : aujourdhui;
}

function estDateExistante(date: unknown): date is DateIso {
  if (typeof date !== 'string') return false;
  try {
    depuisIso(date);
    return true;
  } catch {
    return false;
  }
}

/** Ce qui empêche de créer ce virement, ou null. `aujourdhui` est passé : la règle reste pure. */
export function motifRefusVirementInterne(
  saisie: { emettrice: unknown; beneficiaire: unknown; montant: unknown; date: unknown },
  aujourdhui: DateIso,
): string | null {
  const { emettrice, beneficiaire, montant, date } = saisie;
  if (!estBanqueVirement(emettrice)) return `Choisissez la banque émettrice (${BANQUES_VIREMENT.join(', ')}).`;
  if (!estCompteInterne(beneficiaire)) return `Choisissez la banque bénéficiaire (${COMPTES_INTERNES.join(', ')}).`;
  if (emettrice === beneficiaire) return 'La banque émettrice et la banque bénéficiaire doivent être différentes.';
  if (typeof montant !== 'number' || !Number.isFinite(montant) || montant <= 0) {
    return 'Saisissez un montant supérieur à zéro.';
  }
  if (Math.round(montant * 100) / 100 !== montant) return 'Le montant ne peut pas avoir plus de deux décimales.';
  if (!estDateExistante(date)) return 'Saisissez une date valide.';
  // Refusée à la saisie : on ne prépare pas un virement pour hier. Celle qui se dépasse
  // ensuite, avant l'export, devient le jour de l'export (`dateExecutionInterne`).
  if (date < aujourdhui) return 'La date ne peut pas être passée.';
  return null;
}
